import { useMemo } from 'react';

import { type SankeyLinkWithNodes, type SankeyNodeWithLinks } from './types';

type Props = {
  link: SankeyLinkWithNodes;
  hoveredNode?: SankeyNodeWithLinks;
  onMouseEnter?: () => void;
  onMouseExit?: () => void;
  horizontalScale?: number;
};

const walkUp = (hoveredNode: SankeyNodeWithLinks, node?: SankeyNodeWithLinks): boolean => {
  if (!node) {
    return false;
  }
  if (node.id === hoveredNode?.id) {
    return true;
  }
  return node.targetLinks?.some((link) => walkUp(hoveredNode, link.targetNode)) ?? false;
};

const walkDown = (hoveredNode: SankeyNodeWithLinks, node?: SankeyNodeWithLinks): boolean => {
  if (!node) {
    return false;
  }
  if (node.id === hoveredNode?.id) {
    return true;
  }
  return walkDown(hoveredNode, node.sourceLink?.sourceNode) ?? false;
};

const SimpleSankeyLink = ({
  link,
  hoveredNode,
  onMouseEnter,
  onMouseExit,
  horizontalScale = 1,
}: Props) => {
  const { x0, y0, x1, y1, thickness } = link;
  const halfThickness = thickness / 2;
  const leftMargin = 80;
  const sx0 = leftMargin + (x0 - leftMargin) * horizontalScale;
  const sx1 = leftMargin + (x1 - leftMargin) * horizontalScale;
  const midX = (x0 + x1) / 2;
  const smidX = leftMargin + (midX - leftMargin) * horizontalScale;

  const isEmphasized = useMemo(() => {
    if (!hoveredNode) {
      return true;
    }

    return walkUp(hoveredNode, link.targetNode) || walkDown(hoveredNode, link.sourceNode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredNode?.id]);

  return (
    <path
      key={`${link.sourceNode.id}-${link.targetNode.id}`}
      className="transition-opacity duration-500 ease-in-out"
      d={`
    M${sx0},${y0 - halfThickness}
    C${smidX},${y0 - halfThickness} ${smidX},${y1 - halfThickness} ${sx1},${y1 - halfThickness}
    L${sx1},${y1 + halfThickness}
    C${smidX},${y1 + halfThickness} ${smidX},${y0 + halfThickness} ${sx0},${y0 + halfThickness}
    Z
  `}
      fill={link.targetNode.color}
      opacity={isEmphasized ? 0.5 : 0.1}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseExit}
    />
  );
};

export default SimpleSankeyLink;
